import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import AppLayout from '../../../components/AppLayout';
import { S, Icon, Icons, StatusBadge, BtnPrimary, BtnSecondary, formatDate } from '../../regureStyles';
import { formatFileSize } from '../../../utils/gernalUtils';
import NewClientPanel from '../NewClientPanel';
import PolicyComparisonPanel from './PolicyComparisonPanel';
import PermissionGate from '../../../components/PermissionGate';
import useConfirm from '../../../components/ConfirmDialog/useConfirm';
import { useSelector } from 'react-redux';
import { useFeature } from '../../../hooks/useAccess';
import { productLabel } from '../../../utils/productCatalog';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const ClientDetail = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const history = useHistory();
  const [tab, setTab] = useState('overview');
  // P1-B: the invoices section only exists for tenants with the module,
  // matching the sidebar gating and the endpoint's own requireFeature.
  const invoicingEnabled = useSelector(
    state => state?.tenantConfig?.config?.features?.invoicing !== false,
  );
  // Same rule as the invoices section above, and the same rule the sidebar and
  // the routes apply: a module the tenant does not have leaves no section and
  // no summary tile behind. Found by auditing the other detail pages after the
  // Sponsor/Provider fixes — a client's file on a tenant with offers off
  // rendered an "Offers" heading and an empty state under it, which reads as
  // "you have no offers" rather than "this tenant does not do offers".
  const offersEnabled = useFeature('offers');
  const { confirm, ConfirmPortal } = useConfirm();

  const TYPE_BADGE = {
    individual: { label: t('clientsModule.detail.typeIndividual'), bg: '#e0e7ff', color: '#4338ca' },
    company:    { label: t('clientsModule.detail.typeCompany'),    bg: '#fce7f3', color: '#9d174d' },
  };

  const TABS = [
    { key: 'overview',  label: t('clientsModule.detail.tabOverview') },
    { key: 'documents', label: t('clientsModule.detail.tabDocuments') },
    { key: 'offers',    label: t('clientsModule.detail.tabOffers') },
    { key: 'activity',  label: t('clientsModule.detail.tabActivity') },
  ];

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEdit, setShowEdit] = useState(false);

  const [documents, setDocuments] = useState([]);
  const [offers, setOffers] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [claims, setClaims] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [activity, setActivity] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Feature B — policy comparison selection + result panel + history.
  // selectedDocs holds insertion order so the first checked doc is treated
  // as "expiring" and the second as "renewal" per the prompt's UX spec.
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [comparisons, setComparisons] = useState([]);
  const [activeComparisonId, setActiveComparisonId] = useState(null);
  const [comparing, setComparing] = useState(false);

  const fetchComparisons = useCallback(async () => {
    try {
      const r = await axios.get(`${BASE_URL}/clients/${id}/comparisons`, { withCredentials: true });
      setComparisons(r.data?.data || []);
    } catch { /* endpoint may 404 before deploy */ }
  }, [id]);

  const toggleSelect = (docId) => {
    setSelectedDocs(prev => {
      if (prev.includes(docId)) return prev.filter(x => x !== docId);
      if (prev.length >= 2) return prev; // cap at 2 — Compare button hides anyway
      return [...prev, docId];
    });
  };

  const runCompare = async () => {
    if (selectedDocs.length !== 2 || comparing) return;
    const [expiringDocumentId, renewalDocumentId] = selectedDocs;
    const expName = documents.find(d => d._id === expiringDocumentId)?.originalName || t('clientsModule.detail.expiringFallback');
    const renName = documents.find(d => d._id === renewalDocumentId)?.originalName || t('clientsModule.detail.renewalFallback');
    const ok = await confirm({
      title: t('clientsModule.detail.confirmCompareTitle', { defaultValue: 'Compare policies?' }),
      message: t('clientsModule.detail.confirmCompare', { expiring: expName, renewal: renName }),
      confirmText: t('clientsModule.detail.compare', { defaultValue: 'Compare' }),
      variant: 'primary',
    });
    if (!ok) return;
    setComparing(true);
    try {
      // Pass the requester's UI language so the LLM produces the summary
      // and warnings in French/German/English matching what the reviewer
      // is reading elsewhere on the page (Test Run 7-22: French UI was
      // getting an English comparison body under a French heading).
      const r = await axios.post(
        `${BASE_URL}/clients/${id}/documents/compare`,
        { expiringDocumentId, renewalDocumentId, locale: (i18n?.language || 'en').split('-')[0] },
        { withCredentials: true },
      );
      const newId = r.data?.data?._id;
      if (newId) {
        setActiveComparisonId(newId);
        // Keep selection visible while the comparison runs so the user can
        // still see which doc was the expiring vs renewal. Cleared on close.
        fetchComparisons();
      }
    } catch (e) {
      alert(e?.response?.data?.message || t('clientsModule.detail.compareFailed'));
    } finally {
      setComparing(false);
    }
  };

  const fetchDetail = useCallback(async () => {
    try {
      const resp = await axios.get(`${BASE_URL}/clients/${id}`, { withCredentials: true });
      setData(resp.data?.data || null);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || t('clientsModule.detail.loadError'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchTab = useCallback(async (which) => {
    try {
      if (which === 'documents') {
        const r = await axios.get(`${BASE_URL}/clients/${id}/documents`, { withCredentials: true });
        setDocuments(r.data?.data || []);
        fetchComparisons();
      } else if (which === 'offers') {
        const r = await axios.get(`${BASE_URL}/clients/${id}/offers`, { withCredentials: true });
        setOffers(r.data?.data || []);
        const rp = await axios.get(`${BASE_URL}/clients/${id}/policies`, { withCredentials: true });
        setPolicies(rp.data?.data || []);
        const rc = await axios.get(`${BASE_URL}/clients/${id}/claims`, { withCredentials: true });
        setClaims(rc.data?.data || []);
        // P1-B. Kept in its own try so a tenant without the invoicing
        // module (403) doesn't blank out the offers/policies/claims that
        // already loaded.
        if (invoicingEnabled) {
          try {
            const ri = await axios.get(`${BASE_URL}/clients/${id}/invoices`, { withCredentials: true });
            setInvoices(ri.data?.data || []);
          } catch { setInvoices([]); }
        } else {
          setInvoices([]);
        }
      } else if (which === 'activity') {
        const r = await axios.get(`${BASE_URL}/clients/${id}/activity`, { withCredentials: true });
        setActivity(r.data?.data || []);
      }
    } catch {
      /* per-tab fetch may 404 if endpoint not deployed yet */
    }
  }, [id, fetchComparisons, invoicingEnabled]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);
  useEffect(() => { fetchTab(tab); }, [tab, fetchTab]);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${BASE_URL}/clients/${id}/documents`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await fetchTab('documents');
      await fetchDetail();
    } catch (e) {
      alert(e?.response?.data?.message || t('clientsModule.detail.uploadFailed'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 60 }}>
          <div style={{ width: 36, height: 36, border: '3px solid #e2e8f0', borderTopColor: S.burgundy, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      </AppLayout>
    );
  }

  if (error || !data?.client) {
    return (
      <AppLayout>
        <div style={{ padding: 60, textAlign: 'center' }}>
          <div style={{ fontSize: 16, color: S.textPrimary, marginBottom: 12 }}>{error || t('clientsModule.detail.notFound')}</div>
          <BtnSecondary onClick={() => history.push('/clients')}>{t('clientsModule.detail.backToClients')}</BtnSecondary>
        </div>
      </AppLayout>
    );
  }

  const c = data.client;
  const typeBadge = TYPE_BADGE[c.type] || TYPE_BADGE.individual;

  const card = (children) => (
    <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 12, padding: 18, marginBottom: 16 }}>{children}</div>
  );
  const cardTitle = (title) => (
    <div style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 12 }}>{title}</div>
  );
  const kv = (label, value) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${S.borderLight}`, fontSize: 13 }}>
      <span style={{ color: S.textMuted }}>{label}</span>
      <span style={{ color: S.textPrimary, fontWeight: 500, textAlign: 'right' }}>{value || '—'}</span>
    </div>
  );

  return (
    <AppLayout>
      {showEdit && (
        <NewClientPanel
          client={c}
          onClose={() => setShowEdit(false)}
          onCreated={() => { setShowEdit(false); fetchDetail(); }}
        />
      )}

      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <header style={{ padding: '20px 32px', borderBottom: `1px solid ${S.borderLight}`, background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(20px)' }}>
          <div style={{ fontSize: 12, color: S.textMuted, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <button onClick={() => history.push('/clients')} style={{ background: 'none', border: 'none', color: S.textMuted, cursor: 'pointer', padding: 0, fontSize: 12 }}>{t('clientsModule.detail.breadcrumbClients')}</button>
            {Icons.chevronRight(S.textMuted, 12)}
            <span style={{ color: S.textPrimary, fontWeight: 500 }}>{c.displayName}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: S.textPrimary }}>{c.displayName}</div>
              <span style={{ fontSize: 13, color: S.textMuted, fontFamily: 'monospace' }}>{c.clientNumber}</span>
              <StatusBadge status={c.status} />
              <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: typeBadge.bg, color: typeBadge.color }}>{typeBadge.label}</span>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <BtnSecondary onClick={() => history.push('/clients')}>
                <Icon name='arrow-left' color={S.textMuted} size={15} /> {t('clientsModule.detail.back')}
              </BtnSecondary>
              <PermissionGate resource='clients' action='update'>
                <BtnPrimary onClick={() => setShowEdit(true)}>
                  <Icon name='edit' color='#fff' size={15} /> {t('clientsModule.detail.edit')}
                </BtnPrimary>
              </PermissionGate>
              <PermissionGate resource='clients' action='delete'>
                <BtnSecondary
                  onClick={async () => {
                    const ok = await confirm({
                      title: t('clientsModule.detail.confirmDeleteTitle', { defaultValue: 'Delete client?' }),
                      message: t('clientsModule.detail.confirmDelete', {
                        defaultValue: `"${c.displayName}" will be permanently removed. This cannot be undone.`,
                        name: c.displayName,
                      }),
                      confirmText: t('common.delete', { defaultValue: 'Delete' }),
                      variant: 'danger',
                    });
                    if (!ok) return;
                    try {
                      await axios.delete(`${BASE_URL}/clients/${id}`, { withCredentials: true });
                      history.push('/clients');
                    } catch (e) {
                      alert(e?.response?.data?.message || t('clientsModule.detail.deleteFailed'));
                    }
                  }}
                  style={{ color: '#dc2626', borderColor: '#fecaca' }}
                >
                  <Icon name='trash' color='#dc2626' size={15} /> {t('clientsModule.detail.delete')}
                </BtnSecondary>
              </PermissionGate>
            </div>
          </div>
        </header>

        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 4, padding: '0 32px', borderBottom: `1px solid ${S.borderLight}`, background: '#fff' }}>
          {TABS.map(t => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                style={{ padding: '12px 18px', background: 'transparent', border: 'none', borderBottom: `2px solid ${active ? S.burgundy : 'transparent'}`, fontSize: 13, fontWeight: 700, color: active ? S.burgundy : S.textMuted, cursor: 'pointer' }}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 32, background: '#f9fafb' }}>
          {tab === 'overview' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <div>
                {card(<>
                  {cardTitle(t('clientsModule.detail.sectionContact'))}
                  {kv(t('clientsModule.detail.kvEmail'), c.email)}
                  {kv(t('clientsModule.detail.kvPhone'), c.phone)}
                  {kv(t('clientsModule.detail.kvStreet'), c.address?.street)}
                  {kv(t('clientsModule.detail.kvCity'), c.address?.city)}
                  {kv(t('clientsModule.detail.kvState'), c.address?.state)}
                  {kv(t('clientsModule.detail.kvPostalCode'), c.address?.postalCode)}
                  {kv(t('clientsModule.detail.kvCountry'), c.address?.country)}
                </>)}
                {card(<>
                  {cardTitle(t('clientsModule.detail.sectionClassification'))}
                  {kv(t('clientsModule.detail.kvType'), typeBadge.label)}
                  {kv(t('clientsModule.detail.kvExternalId'), c.externalId)}
                  {kv(t('clientsModule.detail.kvTags'), Array.isArray(c.tags) && c.tags.length ? c.tags.join(', ') : '—')}
                  {kv(t('clientsModule.detail.kvCreated'), formatDate(typeof c.createdAt === 'string' ? c.createdAt.slice(0, 10) : c.createdAt))}
                  {kv(t('clientsModule.detail.kvUpdated'), formatDate(typeof c.updatedAt === 'string' ? c.updatedAt.slice(0, 10) : c.updatedAt))}
                </>)}
              </div>
              <div>
                {card(<>
                  {cardTitle(t('clientsModule.detail.sectionSummary'))}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    {[
                      { label: t('clientsModule.detail.summaryDocuments'), value: data.documentCount || 0, tab: 'documents' },
                      ...(offersEnabled
                        ? [{ label: t('clientsModule.detail.summaryOffers'), value: data.offerCount || 0, tab: 'offers' }]
                        : []),
                      { label: t('clientsModule.detail.summaryPolicies'),  value: data.policyCount   || 0, tab: 'offers' },
                      { label: t('clientsModule.detail.summaryClaims'),    value: data.claimCount    || 0, tab: 'offers' },
                      ...(invoicingEnabled
                        ? [{ label: t('clientsModule.detail.summaryInvoices'), value: data.invoiceCount || 0, tab: 'offers' }]
                        : []),
                    ].map(item => (
                      <button
                        key={item.label}
                        onClick={() => setTab(item.tab)}
                        style={{ padding: 16, background: S.bgCard, border: `1px solid ${S.borderLight}`, borderRadius: 10, cursor: 'pointer', textAlign: 'left' }}
                      >
                        <div style={{ fontSize: 22, fontWeight: 700, color: S.textPrimary }}>{item.value}</div>
                        <div style={{ fontSize: 11, color: S.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6 }}>{item.label}</div>
                      </button>
                    ))}
                  </div>
                </>)}
                {c.notes && card(<>
                  {cardTitle(t('clientsModule.detail.sectionNotes'))}
                  <div style={{ fontSize: 13, color: S.textSecondary, whiteSpace: 'pre-wrap' }}>{c.notes}</div>
                </>)}
              </div>
            </div>
          )}

          {tab === 'documents' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: S.textPrimary }}>{t('clientsModule.detail.docsHeader', { count: documents.length })}</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {selectedDocs.length === 2 && (
                    <BtnPrimary onClick={runCompare} disabled={comparing}>
                      {comparing ? t('clientsModule.detail.starting') : t('clientsModule.detail.compare')}
                    </BtnPrimary>
                  )}
                  <input
                    ref={fileInputRef}
                    type='file'
                    onChange={e => handleUpload(e.target.files?.[0])}
                    style={{ display: 'none' }}
                    id='client-doc-upload'
                  />
                  <PermissionGate resource='documents' action='upload'>
                    <BtnPrimary onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                      <Icon name='upload' color='#fff' size={15} /> {uploading ? t('clientsModule.detail.uploading') : t('clientsModule.detail.uploadDocument')}
                    </BtnPrimary>
                  </PermissionGate>
                </div>
              </div>

              {selectedDocs.length > 0 && selectedDocs.length < 2 && (
                <div style={{ padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 12, color: '#92400e', marginBottom: 12 }}>
                  {t('clientsModule.detail.selectMoreHint')}
                </div>
              )}

              {documents.length === 0 ? (
                <div style={{ padding: 40, background: '#fff', border: `1px dashed ${S.borderLight}`, borderRadius: 12, textAlign: 'center', color: S.textMuted, fontSize: 14 }}>
                  {t('clientsModule.detail.docsEmpty')}
                </div>
              ) : (
                <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 12, overflow: 'hidden' }}>
                  {documents.map((d, i) => {
                    const checked = selectedDocs.includes(d._id);
                    const order = checked ? selectedDocs.indexOf(d._id) + 1 : null;
                    return (
                      <div key={d._id} style={{ padding: '14px 18px', borderBottom: i < documents.length - 1 ? `1px solid ${S.borderLight}` : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: checked ? '#fef9c3' : 'transparent' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <input
                            type='checkbox'
                            checked={checked}
                            onChange={() => toggleSelect(d._id)}
                            disabled={!checked && selectedDocs.length >= 2}
                            style={{ width: 16, height: 16, cursor: 'pointer' }}
                            aria-label={t('clientsModule.detail.selectAria', { name: d.originalName || d.name })}
                          />
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: S.textPrimary, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                              <span>{d.originalName || d.name}</span>
                              {order ? (
                                <span style={{
                                  display: 'inline-block',
                                  padding: '3px 10px',
                                  fontSize: 10,
                                  fontWeight: 800,
                                  letterSpacing: 0.8,
                                  color: '#fff',
                                  background: S.burgundy,
                                  borderRadius: 4,
                                  whiteSpace: 'nowrap',
                                }}>{order === 1 ? t('clientsModule.detail.badgeExpiring') : t('clientsModule.detail.badgeRenewal')}</span>
                              ) : null}
                            </div>
                            <div style={{ fontSize: 12, color: S.textMuted, marginTop: 2 }}>
                              {formatDate(typeof d.createdAt === 'string' ? d.createdAt.slice(0, 10) : d.createdAt)}
                              {(() => { const s = formatFileSize(d.fileSize); return s ? ` · ${s}` : ''; })()}
                              {d.aiClassification?.documentType ? ` · ${d.aiClassification.documentType}` : ''}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                          {(d.fileUrl || d.s3Key || d.contentKey) && (
                            <a href={`/api/v1/documents/${d._id}/view`} target='_blank' rel='noreferrer' style={{ fontSize: 13, color: S.burgundy, textDecoration: 'underline' }}>{t('clientsModule.detail.view')}</a>
                          )}
                          {/* D-12: client document delete affordance was
                              absent. Backend DELETE /clients/:id/documents/:docId
                              was already there; parent screen refetches
                              the tab on success. Confirms via
                              window.confirm — matches the pattern used
                              elsewhere in the client CRUD flow. */}
                          <button
                            type='button'
                            onClick={async (e) => {
                              e.stopPropagation();
                              const label = d.originalName || d.name || t('clientsModule.detail.view');
                              const ok = await confirm({
                                title: t('clientsModule.detail.confirmDeleteDocTitle', { defaultValue: 'Delete document?' }),
                                message: t('clientsModule.detail.confirmDeleteDoc', {
                                  defaultValue: `"${label}" will be permanently removed. This cannot be undone.`,
                                  name: label,
                                }),
                                confirmText: t('common.delete', { defaultValue: 'Delete' }),
                                variant: 'danger',
                              });
                              if (!ok) return;
                              try {
                                await axios.delete(`${BASE_URL}/clients/${id}/documents/${d._id}`, { withCredentials: true });
                                fetchTab('documents');
                              } catch (err) {
                                console.error('delete document failed', err);
                                window.alert(err?.response?.data?.message || t('clientsModule.detail.deleteDocFailed', { defaultValue: 'Failed to delete document.' }));
                              }
                            }}
                            style={{
                              fontSize: 13,
                              color: '#dc2626',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: 0,
                              textDecoration: 'underline',
                            }}
                          >
                            {t('clientsModule.detail.delete', { defaultValue: 'Delete' })}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Active comparison panel — opens above the history list */}
              {activeComparisonId && (
                <div style={{ marginTop: 20 }}>
                  <PolicyComparisonPanel
                    clientId={id}
                    comparisonId={activeComparisonId}
                    onClose={() => { setActiveComparisonId(null); setSelectedDocs([]); fetchComparisons(); }}
                  />
                </div>
              )}

              {/* Previous Comparisons */}
              {!activeComparisonId && comparisons.length > 0 && (
                <div style={{ marginTop: 24 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10 }}>{t('clientsModule.detail.prevComparisons', { count: comparisons.length })}</div>
                  <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 12, overflow: 'hidden' }}>
                    {comparisons.map((row, i) => {
                      const exp = row.expiringDocumentId;
                      const ren = row.renewalDocumentId;
                      const expName = exp?.originalName || exp?.name || t('clientsModule.comparison.expiringFallback');
                      const renName = ren?.originalName || ren?.name || t('clientsModule.comparison.renewalFallback');
                      const summary = row.comparison?.summary || (row.status === 'failed' ? (row.errorMessage || t('clientsModule.detail.statusFailed')) : t('clientsModule.detail.statusProcessing'));
                      const statusColor = row.status === 'completed' ? '#059669' : row.status === 'failed' ? '#dc2626' : '#92400e';
                      return (
                        <button
                          key={row._id}
                          onClick={() => setActiveComparisonId(row._id)}
                          style={{ width: '100%', textAlign: 'left', padding: '14px 18px', background: 'transparent', border: 'none', borderBottom: i < comparisons.length - 1 ? `1px solid ${S.borderLight}` : 'none', cursor: 'pointer' }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: S.textPrimary, marginBottom: 4 }}>
                                {expName} <span style={{ color: S.textMuted, fontWeight: 400 }}>{t('clientsModule.detail.vs')}</span> {renName}
                              </div>
                              <div style={{ fontSize: 12, color: S.textMuted, lineHeight: 1.4 }}>{summary}</div>
                            </div>
                            <div style={{ fontSize: 11, color: statusColor, fontWeight: 700, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{row.status}</div>
                          </div>
                          <div style={{ fontSize: 11, color: S.textMuted, marginTop: 6 }}>
                            {row.comparedAt ? new Date(row.comparedAt).toLocaleString() : formatDate(typeof row.createdAt === 'string' ? row.createdAt.slice(0, 10) : row.createdAt)}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === 'offers' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              {/* D-11: rows in these sections had no click handler.
                  Now clicking an offer/policy/claim navigates to the
                  respective screen with a query param the target
                  screen can use to pre-select the row. Falls back to
                  the list root when the row has no _id (shouldn't
                  happen but is defensive). */}
              {offersEnabled && (
                <Section title={t('clientsModule.detail.offersHeader', { count: offers.length })} items={offers.map(o => ({
                  primary: o.offerNumber,
                  // P1-D: never render a stored product string raw — legacy
                  // offers carry an English label that cannot follow the
                  // interface language. productLabel() maps it back to a code.
                  secondary: `${productLabel(t, o.product || o.lineOfBusiness, o.lineOfBusiness, 'short')} · ${o.status}`,
                  tertiary: formatDate(typeof o.createdAt === 'string' ? o.createdAt.slice(0, 10) : o.createdAt),
                  onClick: () => history.push(o._id ? `/offers?open=${o._id}` : '/offers'),
                }))} emptyText={t('clientsModule.detail.offersEmpty')} />
              )}
              <Section title={t('clientsModule.detail.policiesHeader', { count: policies.length })} items={policies.map(p => ({
                primary: p.policyNumber,
                secondary: `${productLabel(t, p.product, p.lineOfBusiness, 'short')} · ${p.status}`,
                tertiary: formatDate(typeof p.createdAt === 'string' ? p.createdAt.slice(0, 10) : p.createdAt),
                onClick: () => history.push(p._id ? `/policies/${p._id}` : '/policies'),
              }))} emptyText={t('clientsModule.detail.policiesEmpty')} />
              <Section title={t('clientsModule.detail.claimsHeader', { count: claims.length })} items={claims.map(cl => ({
                primary: cl.claimNumber,
                secondary: `${cl.lineOfBusiness || ''} · ${cl.status}`,
                tertiary: formatDate(typeof cl.createdAt === 'string' ? cl.createdAt.slice(0, 10) : cl.createdAt),
                onClick: () => history.push(cl._id ? `/claims/${cl._id}` : '/claims'),
              }))} emptyText={t('clientsModule.detail.claimsEmpty')} />
              {/* P1-B: invoices were created, linked and correct — and
                  invisible, because nothing on the client's file read
                  them. Amount is the number the user came here for, so it
                  leads the row. */}
              {invoicingEnabled && (
                <Section title={t('clientsModule.detail.invoicesHeader', { count: invoices.length })} items={invoices.map(inv => ({
                  primary: inv.invoiceNumber,
                  secondary: `${formatMoney(inv.amount, inv.currency)} · ${inv.status}`,
                  tertiary: inv.dueDate
                    ? t('clientsModule.detail.invoiceDue', { date: formatDate(String(inv.dueDate).slice(0, 10)) })
                    : formatDate(typeof inv.createdAt === 'string' ? inv.createdAt.slice(0, 10) : inv.createdAt),
                  onClick: () => history.push('/invoicing'),
                }))} emptyText={t('clientsModule.detail.invoicesEmpty')} />
              )}
            </div>
          )}

          {tab === 'activity' && (
            <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 12, overflow: 'hidden' }}>
              {activity.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: S.textMuted, fontSize: 14 }}>{t('clientsModule.detail.activityEmpty')}</div>
              ) : activity.map((ev, i) => (
                <div key={i} style={{ padding: '14px 18px', borderBottom: i < activity.length - 1 ? `1px solid ${S.borderLight}` : 'none' }}>
                  <div style={{ fontSize: 13, color: S.textPrimary }}>{ev.description}</div>
                  <div style={{ fontSize: 11, color: S.textMuted, marginTop: 4 }}>{formatDate(typeof ev.at === 'string' ? ev.at.slice(0, 10) : ev.at)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {ConfirmPortal}
    </AppLayout>
  );
};

// Compact money for list rows. Intl handles the tenant's separators; the
// currency comes off the invoice so a multi-currency tenant reads right.
const formatMoney = (amount, currency) => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency', currency: currency || 'CHF', maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${currency || 'CHF'} ${n.toLocaleString()}`;
  }
};

const Section = ({ title, items, emptyText }) => (
  <div>
    <div style={{ fontSize: 13, fontWeight: 700, color: S.textPrimary, marginBottom: 10 }}>{title}</div>
    {items.length === 0 ? (
      <div style={{ padding: 24, background: '#fff', border: `1px dashed ${S.borderLight}`, borderRadius: 10, textAlign: 'center', color: S.textMuted, fontSize: 13 }}>{emptyText}</div>
    ) : (
      <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 12, overflow: 'hidden' }}>
        {items.map((it, i) => (
          <div
            key={i}
            onClick={it.onClick}
            style={{
              padding: '12px 16px',
              borderBottom: i < items.length - 1 ? `1px solid ${S.borderLight}` : 'none',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: it.onClick ? 'pointer' : 'default',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => { if (it.onClick) e.currentTarget.style.background = '#f8fafc'; }}
            onMouseLeave={e => { if (it.onClick) e.currentTarget.style.background = 'transparent'; }}
          >
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: S.textPrimary }}>{it.primary}</div>
              <div style={{ fontSize: 12, color: S.textMuted, marginTop: 2 }}>{it.secondary}</div>
            </div>
            <div style={{ fontSize: 12, color: S.textMuted }}>{it.tertiary}</div>
          </div>
        ))}
      </div>
    )}
  </div>
);

export default ClientDetail;
